import { TbX, TbArrowLeft, TbChevronRight, TbEdit } from "react-icons/tb";
import type { TaskGeometry } from "@/types/task";
import type { Task } from "@/types/task";

interface TaskFormHeaderProps {
  isEditing: boolean;
  onClose: () => void;
  geometry?: TaskGeometry;
  customBreadcrumbs?: string[];
  showBackButton?: boolean;
  onBack?: () => void;
  title?: string;
  isViewMode?: boolean;
  task?: Task;
  onEditTask?: (task: Task) => void;
}

export const TaskFormHeader: React.FC<TaskFormHeaderProps> = ({
  isEditing,
  onClose,
  geometry,
  customBreadcrumbs,
  showBackButton = false,
  onBack,
  title,
  isViewMode = false,
  task,
  onEditTask,
}) => {
  // Convert geometry type to display name
  const getGeometryDisplayName = (geometry?: TaskGeometry): string => {
    if (!geometry?.type) return "Point";

    switch (geometry.type) {
      case "Point":
        return "Point";
      case "LineString":
        return "Line";
      case "Polygon":
        return "Polygon";
      default:
        return "Point";
    }
  };

  // Use custom breadcrumbs if provided, otherwise use default
  const breadcrumbs = customBreadcrumbs || [
    "Location Type",
    getGeometryDisplayName(geometry),
    isEditing ? "Edit Task" : isViewMode ? "View Task" : "Create New Task",
  ];

  return (
    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
      <div className="flex gap-2 items-center">
        {showBackButton && onBack && (
          <button
            onClick={onBack}
            className="w-10 h-10 p-2.5 bg-gray-100 inline-flex cursor-pointer justify-start items-center gap-2.5 hover:bg-gray-100 rounded-full transition-colors"
          >
            <TbArrowLeft size={20} className="text-gray-600" />
          </button>
        )}
        <div>
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-1 text-[10px] text-[#828282] font-medium mb-1">
            {breadcrumbs.map((crumb, index) => (
              <div key={index} className="flex items-center">
                {index > 0 && (
                  <TbChevronRight size={12} className="mx-1 text-[#828282]" />
                )}
                <span
                  className={
                    index === breadcrumbs.length - 1
                      ? "text-[#444444] text-[12px] font-semibold"
                      : ""
                  }
                >
                  {crumb}
                </span>
              </div>
            ))}
          </nav>

          {/* Title - show if provided or default based on breadcrumbs */}
          <h1 className="text-[22px] font-bold text-[#444444] leading-tight">
            {title ||
              (isEditing ? "Edit Task" : breadcrumbs[breadcrumbs.length - 1])}
          </h1>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2">
        {/* Show edit button in view mode */}
        {isViewMode && task && onEditTask && (
          <button
            onClick={() => onEditTask(task)}
            className="text-gray-400 transition-colors rounded-full p-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300 cursor-pointer"
            title="Edit"
          >
            <TbEdit size={22} />
          </button>
        )}

        {/* Close button */}
        <button
          onClick={onClose}
          className="text-gray-400 transition-colors rounded-full p-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-300 cursor-pointer"
          title="Close"
        >
          <TbX size={22} />
        </button>
      </div>
    </div>
  );
};
